import { Box, ButtonBase } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import colors from "../../config/colors";
import { colorPalette } from "../../config/colorPalette";
import { ELEVATION } from "../../config/shadows";

// MUI icons sit in a padded box, so 18px reads like Figma's 15px glyph.
const iconBoxSx = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  width: 32,
  height: 32,
  fontSize: 18,
  color: colorPalette.mercuryAlpha[65],
  "& svg": { fontSize: "inherit", width: "1em", height: "1em" },
};

const VARIANTS = {
  filled: {
    backgroundColor: colorPalette.mercuryAlpha[8],
    border: "none",
    padding: "0 6px",
    // Not in Figma: a subtle border so you can tell the field is focused
    "&:focus-within": {
      boxShadow: `inset 0 0 0 1px ${colorPalette.mercuryAlpha[18]}`,
    },
  },
  elevated: {
    backgroundColor: colors.white,
    // Figma's 1px stroke sits inside the box, so trim 1px from the padding
    border: `1px solid ${colorPalette.mercuryAlpha[8]}`,
    boxShadow: ELEVATION,
    padding: "0 5px",
    "&:focus-within": { borderColor: colorPalette.mercuryAlpha[18] },
  },
};

/**
 * SearchField
 *
 * Props:
 * - variant: filled | elevated
 * - startIcon: node before the input (default: search icon)
 * - endIcon: node after the input, behind a divider (e.g. a mic icon)
 * - onEndIconClick / endIconLabel: make the end icon a button (label is required for a11y)
 * - sx: extra styles for the container, merged last
 * - anything else (value, onChange, placeholder, ...) goes to the input
 */
const SearchField = ({
  variant = "filled",
  startIcon = <SearchIcon />,
  endIcon,
  onEndIconClick,
  endIconLabel,
  sx: sxOverride = {},
  ...rest
}) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: "10px",
      height: 40,
      boxSizing: "border-box",
      borderRadius: "12px",
      ...(VARIANTS[variant] || VARIANTS.filled),
      ...sxOverride,
    }}
  >
    <Box component="span" aria-hidden="true" sx={iconBoxSx}>
      {startIcon}
    </Box>
    <Box
      component="input"
      type="search"
      {...rest}
      sx={{
        flex: 1,
        minWidth: 0,
        padding: 0,
        border: "none",
        outline: "none",
        background: "transparent",
        fontFamily: '"Lexend", sans-serif',
        fontSize: 14,
        fontStyle: "normal",
        fontWeight: 400,
        lineHeight: 1.45,
        letterSpacing: "0.1px",
        color: colorPalette.mercury[950],
        "&::placeholder": { color: colorPalette.mercuryAlpha[55], opacity: 1 },
        "&::-webkit-search-cancel-button": { display: "none" },
      }}
    />
    {endIcon && (
      <Box sx={{ display: "flex", alignItems: "center", gap: "2px" }}>
        <Box
          aria-hidden="true"
          sx={{
            width: "1px",
            height: 20,
            backgroundColor: colorPalette.mercuryAlpha[8],
          }}
        />
        {onEndIconClick ? (
          <ButtonBase
            aria-label={endIconLabel}
            onClick={onEndIconClick}
            sx={{ ...iconBoxSx, borderRadius: "8px" }}
          >
            {endIcon}
          </ButtonBase>
        ) : (
          <Box component="span" aria-hidden="true" sx={iconBoxSx}>
            {endIcon}
          </Box>
        )}
      </Box>
    )}
  </Box>
);

export default SearchField;
